import { SetStateAction, useEffect, useState} from 'react'
import {Dimensions, ScaledSize} from 'react-native'
import moment from 'moment'
import {availableSoundOptions} from '../common/audio/focusSeshSounds'
import {FocusSeshPreset} from "../../reducers/student/focusSeshPresetsSlice";

export const coalesce = (...args: (string | number)[]) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const arg of args) {
    if (arg !== undefined && arg !== null) return arg
  }
  return undefined
}

export const getMinutesSeconds = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return [minutes, remainingSeconds]
}

export const useScreenDimensions = () => {
  const [screenData, setScreenData] = useState(Dimensions.get('screen'))

  useEffect(() => {
    const onChange = (result: { screen: SetStateAction<ScaledSize> }) => {
      setScreenData(result.screen)
      console.log('screen changed')
    }

    const subscription = Dimensions.addEventListener('change', onChange)
    return () => {
      // @ts-ignore
      subscription.remove('change', onChange)
    }
  })

  return [screenData]
}

export const setTimeoutForTest = (handler: any, timeout: number, ...rest: any[]) => {
  if (process.env.NODE_ENV === 'test') {
    return setTimeout(handler, 0, ...rest)
  } else {
    return setTimeout(handler, timeout, ...rest)
  }
}

export const getStartOfDayXDaysAgo = (numDaysAgo: number) => {
  let startOfDay: string
  if (numDaysAgo > 0) {
    startOfDay = moment().subtract(numDaysAgo, 'days').startOf('day').toISOString()
  } else if (numDaysAgo === 0) {
    startOfDay = moment().startOf('day').toISOString()
  }
  return startOfDay
}

export const getStartOfDayForDate = (date: string) => {
  return moment(date).startOf('day').toISOString()
}

export const getEndOfDayForDate = (date: string) => {
  return moment(date).endOf('day').toISOString()
}

export const getTotalFocusSeshTimeInMinutes = (logs: any[]) => {
  return Math.round(logs.reduce((sum, log) => log.secondsDuration + sum, 0) / 60)
}

// Returns 'NUM min' if total time is less than 60 mins
// Returns 'NUM hr' rounded to nearest half hour if total time is more than 60 mins
export const getTotalFocusSeshTimeInMinutesOrHours = (logs: any[]) => {
  const totalTimeInMinutes = Math.round(logs.reduce((sum, log) => log.secondsDuration + sum, 0) / 60)
  let totalTime: string
  if (totalTimeInMinutes < 60) {
    totalTime = `${totalTimeInMinutes} min`
  } else if (totalTimeInMinutes >= 60) {
    // Calculate total minutes into hours, rounded to the nearest half hour
    const totalTimeInHours = Math.round(totalTimeInMinutes / 30) / 2
    totalTime = `${totalTimeInHours} hr`
  }
  return totalTime
}

const isSoundAvailable = (soundKey: string, soundType: string) => {
  if (soundType === 'checkIn') {
    return soundKey === 'N/A' || availableSoundOptions.some(
      sound => sound.isCheckInSound && sound.checkInSound === soundKey
    )
  }

  if (soundType === 'soundtrack') {
    return soundKey === 'None' || availableSoundOptions.some(
      sound => sound.isSoundtrack && sound.soundtrack === soundKey
    )
  }

  return false
}

export const filterForAvailablePresets = (presets: FocusSeshPreset[]) => presets.filter((preset) => {
  const checkInSoundAvailable = isSoundAvailable(preset.checkInChime, 'checkIn')
  const soundtrackAvailable = isSoundAvailable(preset.soundtrack, 'soundtrack')
  return checkInSoundAvailable && soundtrackAvailable
})

export const getGroupsFromTimestampedCaptions = async (captions: any[]) => {
  const captionGroups = []
  let workingGroup = {
    start: null, // Will be set when first word is added
    words: [],
  }

  for (let i = 0; i < captions.length; i++) {
    const word = captions[i]
    const { word: text, start, end } = word

    // skip empty words. Will concat with spaces
    if (text.trim() === '') continue

    // Calculate metrics for current group + this word
    const testGroup = {
      ...workingGroup,
      words: [...workingGroup.words, word]
    }

    // Set start time to first word's start if this is the first word in the group
    if (workingGroup.words.length === 0) {
      testGroup.start = start
    }

    const duration = end - testGroup.start
    const charCount = testGroup.words.map((w) => w.text).join(' ').length

    // Check for natural break points
    const isSentenceEnd = text.match(/[.!?]$/)
    const isPhraseEnd = text.match(/[,;:]$/)
    const isLongPause = i < captions.length - 1 &&
      captions[i + 1].start - end > 0.3 // 400ms+ pause

    // Force break conditions (violate best practices)
    const tooLong = duration > 2.5 // Max 2.5 seconds
    const tooManyChars = charCount > 30 // Max 30 characters
    const tooShortForBreak = duration < 0.3 // Don't break if under 0.3 second

    // Natural break opportunities
    const goodSentenceBreak = isSentenceEnd && !tooShortForBreak
    const goodPhraseBreak = isPhraseEnd && duration >= 1
    const goodPauseBreak = isLongPause && duration >= 1

    // Decision logic
    if (tooLong || tooManyChars) {
      // MUST break - violating constraints
      captionGroups.push(workingGroup)
      workingGroup = {
        start: start, // Start of current word
        words: [word],
      }
    } else if (goodSentenceBreak || goodPhraseBreak || goodPauseBreak) {
      // SHOULD break - natural opportunity
      workingGroup = testGroup // Include this word
      captionGroups.push(workingGroup)
      workingGroup = {
        start: null, // Will be set when next word is added
        words: [],
      }
    } else {
      // Continue building current group
      workingGroup = testGroup
    }
  }

  // Add final group if it has words
  if (workingGroup.words.length > 0) {
    captionGroups.push(workingGroup)
  }

  // Post-processing: merge very short captions with next one
  const finalGroups = []
  for (let i = 0; i < captionGroups.length; i++) {
    const group = captionGroups[i]
    const nextGroup = captionGroups[i + 1]
    const groupDuration = group.words[group.words.length - 1].end - group.start

    if (groupDuration < 1 && nextGroup &&
      nextGroup.start - group.words[group.words.length - 1].end < 0.3) {
      // Merge with next group - start time should be the earlier start time
      const mergedGroup = {
        start: group.start, // Keep the original start time
        words: [...group.words, ...nextGroup.words]
      }
      finalGroups.push(mergedGroup)
      i++ // Skip next group since we merged it
    } else {
      finalGroups.push(group)
    }
  }

  return finalGroups
}

export const parseRouteAndParamsFromUrl = (url: string): { route: string; params: Record<string, string> } => {
  if (!url) return { route: '', params: {} }

  if (!url.includes('?')) {
    return { route: url, params: {} }
  }

  const route = url.split('?')[0] // in case there are query params
  const params = url.split('?')[1] ? Object.fromEntries(new URLSearchParams(url.split('?')[1])) : {}
  return { route, params }
}
